import { useEffect, useState } from 'react';
import type { Position, Rapport, Uebergang } from '@arbeitsrapporte/shared';
import { api } from '../api';
import { useIch } from '../App';
import { Fehler } from '../bausteine/Meldung';
import { Statusmarke } from '../bausteine/Statusmarke';
import { ART_TEXT, datumLesbar, summen, UEBERGANG_TEXT, zeitpunktLesbar } from '../format';
import { navigiere } from '../router';

/**
 * Die Positionen, wie sie auf dem Rapport stehen – und darunter, was
 * zusammenkommt. Auch auf der Unterschriftsseite: Der Kunde soll sehen,
 * was er unterschreibt.
 */
export function Positionstabelle({ positionen }: { positionen: readonly Position[] }) {
  const gesamt = summen(positionen);

  if (positionen.length === 0) {
    return <p className="text-slate-600">Keine Positionen.</p>;
  }

  return (
    /*
      Waagerecht scrollbar statt umgebrochen: Auf dem Telefon ist die
      Mengenspalte sonst die erste, die zerfällt – und gerade sie will
      man lesen.
    */
    <div className="overflow-x-auto rounded border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="px-3 py-2 font-medium">Art</th>
            <th className="px-3 py-2 font-medium">Bezeichnung</th>
            <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Menge</th>
          </tr>
        </thead>
        <tbody>
          {positionen.map((p) => (
            <tr
              key={p.nummer}
              className={`border-b border-slate-100 last:border-0 ${
                p.verrechenbar ? '' : 'text-slate-400'
              }`}
            >
              <td className="px-3 py-2 align-top whitespace-nowrap">{ART_TEXT[p.art]}</td>
              <td className="px-3 py-2 align-top">
                <span className={p.verrechenbar ? 'text-slate-900' : ''}>{p.bezeichnung}</span>
                <span className="text-slate-500">
                  {p.von && p.bis && ` ${p.von}–${p.bis}`}
                  {p.taetigkeitsartBezeichnung && ` · ${p.taetigkeitsartBezeichnung}`}
                  {p.artikelNummer && ` · ${p.artikelNummer}`}
                  {p.auftragspositionNummer && ` · Pos. ${p.auftragspositionNummer}`}
                  {!p.verrechenbar && ' · nicht verrechenbar'}
                </span>
                {p.bemerkung && <div className="text-slate-500">{p.bemerkung}</div>}
              </td>
              <td className="px-3 py-2 text-right align-top whitespace-nowrap">
                {p.menge} {p.einheit}
              </td>
            </tr>
          ))}
        </tbody>
        {/*
          Die Summe je Einheit, nur die verrechenbaren Stunden und Kilometer:
          Leitung und Kunde sollen nicht im Kopf addieren.
        */}
        {gesamt.length > 0 && (
          <tfoot>
            <tr className="border-t border-slate-200 font-medium text-slate-900">
              <td className="px-3 py-2" colSpan={2}>
                Summe verrechenbar
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {gesamt.map((s) => `${s.menge} ${s.einheit}`).join(' · ')}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/**
 * Ein Übergang, zu dem etwas gesagt wird – zurückgeben und stornieren.
 *
 * Kein `window.confirm`: Das fragt nur „wirklich?" und lässt keinen Grund
 * zu. Der Grund ist der Punkt – der Techniker soll lesen, was fehlt, statt
 * anzurufen; und am stornierten Rapport soll stehen, warum.
 */
function MitGrund({
  uebergang,
  pflicht,
  hinweis,
  laeuft,
  onAusfuehren,
  onAbbrechen,
}: {
  uebergang: 'zurueckgeben' | 'stornieren';
  pflicht: boolean;
  hinweis: string;
  laeuft: boolean;
  onAusfuehren: (grund: string) => void;
  onAbbrechen: () => void;
}) {
  const [grund, setGrund] = useState('');
  const gefahr = uebergang === 'stornieren';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onAusfuehren(grund.trim());
      }}
      className={`mt-4 space-y-3 rounded border p-3 ${
        gefahr ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <p className={`text-sm ${gefahr ? 'text-rose-900' : 'text-slate-600'}`}>{hinweis}</p>
      <div>
        <label className="feldname" htmlFor="uebergang-grund">
          Grund{pflicht ? '' : ' (freiwillig)'}
        </label>
        <input
          id="uebergang-grund"
          autoFocus
          required={pflicht}
          className="feld"
          maxLength={500}
          placeholder={
            uebergang === 'stornieren'
              ? 'z. B. doppelt angelegt, Einsatz abgesagt'
              : 'z. B. Unterschrift fehlt, Position 2 doppelt'
          }
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          className={gefahr ? 'knopf-gefahr' : 'knopf-haupt'}
          disabled={laeuft || (pflicht && !grund.trim())}
        >
          {UEBERGANG_TEXT[uebergang]}
        </button>
        <button type="button" className="knopf" onClick={onAbbrechen}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

/**
 * Die Einzelansicht. Welche Knöpfe es gibt, sagt `erlaubt` – die
 * Oberfläche baut die Regeln nicht nach.
 */
export function RapportSeite({ id }: { id: string }) {
  const ich = useIch();
  const [r, setR] = useState<Rapport | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [gefragt, setGefragt] = useState<'zurueckgeben' | 'stornieren' | null>(null);
  /** Nach der Freigabe: der nächste, der wartet – oder `null`, wenn keiner mehr. */
  const [naechster, setNaechster] = useState<{ id: string; nummer: string } | null | undefined>();

  useEffect(() => {
    setNaechster(undefined);
    setGefragt(null);
    api.rapport(id).then(setR).catch(setFehler);
  }, [id]);

  if (fehler && !r) return <Fehler fehler={fehler} />;
  if (!r) return <p className="text-slate-600">Lädt …</p>;

  const tue = async (arbeit: () => Promise<Rapport>) => {
    setLaeuft(true);
    setFehler(null);

    try {
      const danach = await arbeit();

      setR(danach);
      setGefragt(null);

      /*
       * Die Leitung gibt am Freitag zwanzig frei. Nach jedem soll der Weg
       * zum nächsten ein Griff sein – nicht zurück zur Liste und wieder
       * hinein.
       */
      if (danach.status === 'freigegeben') {
        const { daten } = await api.rapporte({ status: ['abgeschlossen'] });
        const weiterer = daten.find((k) => k.id !== danach.id);

        setNaechster(weiterer ? { id: weiterer.id, nummer: weiterer.nummer } : null);
      }
    } catch (f) {
      setFehler(f);
    } finally {
      setLaeuft(false);
    }
  };

  const uebergang = (u: Uebergang, grund?: string) => {
    if ((u === 'stornieren' || u === 'zurueckgeben') && grund === undefined) {
      setGefragt(u);

      return;
    }

    void tue(() => api.uebergang(r.id, u, grund));
  };

  const darfUnterschreiben = r.erlaubt.aendern && r.status === 'entwurf';
  const istEigener = r.mitarbeiter.id === ich.mitarbeiter.id;

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-2xl font-semibold">{r.nummer}</h1>
        <Statusmarke status={r.status} />
      </div>

      <Fehler fehler={fehler} />

      {/*
        Was zu diesem Rapport gesagt wurde – der Grund der Rückgabe, solange
        er Entwurf ist; der Grund des Stornos für immer.
      */}
      {r.status === 'entwurf' && r.zurueckgabeGrund && (
        <p className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
          <span className="font-medium">Zurückgegeben:</span> {r.zurueckgabeGrund}
        </p>
      )}
      {r.status === 'storniert' && r.stornoGrund && (
        <p className="mb-4 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900">
          <span className="font-medium">Storniert:</span> {r.stornoGrund}
        </p>
      )}

      {naechster !== undefined && (
        <p className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-emerald-900">
          <span>Freigegeben – der Rapport geht an das ERP.</span>
          {naechster ? (
            <a
              href={`/rapport/${naechster.id}`}
              className="font-medium underline underline-offset-4 hover:text-emerald-950"
            >
              Nächster zur Freigabe: {naechster.nummer}
            </a>
          ) : (
            <a
              href="/freigabe"
              className="font-medium underline underline-offset-4 hover:text-emerald-950"
            >
              Nichts mehr zur Freigabe – zur Liste
            </a>
          )}
        </p>
      )}

      {/*
        Die Kopfdaten als Definitionsliste und nicht als Tabelle: Es sind
        Paare, keine Spalten. Auf dem Telefon untereinander, ab `sm` in zwei
        Spalten – sonst stünde „Auftrag" allein in einer Zeile und der Wert
        darunter.
      */}
      <dl className="karte grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
        <dt className="text-sm text-slate-500">Auftrag</dt>
        <dd className="text-slate-900">
          {/*
            Der Auftrag führt in die Auftragsabwicklung, in einem neuen Tab –
            dort steht, was zu ihm gehört. Ohne eingetragene Adresse bleibt
            er Text statt eines Links, der ins Leere zeigt.
          */}
          {ich.abwicklungAdresse ? (
            <a
              href={`${ich.abwicklungAdresse}/auftraege/${r.auftragId}`}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4 hover:text-slate-700"
            >
              {r.auftragNummer} {r.auftragBezeichnung}
            </a>
          ) : (
            <>
              {r.auftragNummer} {r.auftragBezeichnung}
            </>
          )}
        </dd>
        <dt className="text-sm text-slate-500">Kunde</dt>
        <dd className="text-slate-900">{r.kundeName || '–'}</dd>
        <dt className="text-sm text-slate-500">Datum</dt>
        <dd className="text-slate-900">{datumLesbar(r.datum)}</dd>
        <dt className="text-sm text-slate-500">Techniker</dt>
        <dd className="text-slate-900">{r.mitarbeiter.name}</dd>
        {r.einsatzort && (
          <>
            <dt className="text-sm text-slate-500">Einsatzort</dt>
            <dd className="text-slate-900">{r.einsatzort}</dd>
          </>
        )}
        {r.ansprechpartner && (
          <>
            <dt className="text-sm text-slate-500">Ansprechpartner</dt>
            <dd className="text-slate-900">{r.ansprechpartner}</dd>
          </>
        )}
      </dl>

      {r.taetigkeit && (
        <>
          <h2 className="mt-6 mb-2 text-lg font-semibold">Ausgeführte Arbeiten</h2>
          <p className="karte whitespace-pre-wrap text-slate-900">{r.taetigkeit}</p>
        </>
      )}

      <h2 className="mt-6 mb-2 text-lg font-semibold">Positionen</h2>
      <Positionstabelle positionen={r.positionen} />

      {r.bemerkung && (
        <>
          <h2 className="mt-6 mb-2 text-lg font-semibold">Interne Bemerkung</h2>
          <p className="karte whitespace-pre-wrap text-slate-900">{r.bemerkung}</p>
        </>
      )}

      <h2 className="mt-6 mb-2 text-lg font-semibold">Unterschrift des Kunden</h2>
      {r.unterschrift ? (
        <div className="karte">
          <img
            src={r.unterschrift.bild}
            alt="Unterschrift"
            className="max-h-24 rounded border border-slate-200 bg-white p-2"
          />
          <div className="mt-2 text-sm text-slate-600">
            {r.unterschrift.name}, {zeitpunktLesbar(r.unterschrift.zeitpunkt)}
          </div>
          {darfUnterschreiben && (
            <button
              type="button"
              disabled={laeuft}
              onClick={() => tue(() => api.entferneUnterschrift(r.id))}
              className="knopf-gefahr mt-3"
            >
              Unterschrift entfernen
            </button>
          )}
        </div>
      ) : (
        <p className="text-slate-600">Noch nicht unterschrieben.</p>
      )}

      {/*
        Warum „Abschließen" fehlt, obwohl der Rapport ein Entwurf ist: Der
        Dienst sagt es (`erlaubt.abschlussHindernis`), die Seite schreibt es
        hin – statt den Knopf zu zeigen und die Ablehnung abzuwarten.
      */}
      {r.erlaubt.abschlussHindernis && (
        <p className="mt-6 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {r.erlaubt.abschlussHindernis}
          {r.erlaubt.aendern &&
            !r.unterschrift &&
            ' Unter „Bearbeiten" lassen sich Positionen erfassen.'}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {r.erlaubt.aendern && !r.unterschrift && (
          <button
            type="button"
            onClick={() => navigiere(`/rapport/${r.id}/bearbeiten`)}
            className="knopf"
          >
            Bearbeiten
          </button>
        )}
        {darfUnterschreiben && !r.unterschrift && (
          <button
            type="button"
            onClick={() => navigiere(`/rapport/${r.id}/unterschrift`)}
            className="knopf"
          >
            Unterschreiben lassen
          </button>
        )}
        {r.erlaubt.uebergaenge.map((u) => (
          <button
            key={u}
            type="button"
            disabled={laeuft}
            aria-expanded={gefragt === u ? true : undefined}
            onClick={() => uebergang(u)}
            className={
              u === 'abschliessen' || u === 'freigeben'
                ? 'knopf-haupt'
                : u === 'stornieren'
                  ? 'knopf-gefahr'
                  : 'knopf'
            }
          >
            {UEBERGANG_TEXT[u]}
          </button>
        ))}
        <button
          type="button"
          onClick={() => api.oeffneDruck(r.id).catch(setFehler)}
          className="ml-auto min-h-0 px-2 py-1 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
        >
          Drucken / PDF
        </button>
      </div>

      {gefragt === 'zurueckgeben' && (
        <MitGrund
          uebergang="zurueckgeben"
          /*
           * Wer einen fremden Rapport zurückgibt, sagt warum – der Techniker
           * liest es am Entwurf. Wer seinen eigenen zurückholt, weiß es.
           */
          pflicht={!istEigener}
          hinweis={
            istEigener
              ? 'Der Rapport wird wieder ein Entwurf und lässt sich ändern.'
              : `${r.mitarbeiter.name} sieht den Grund am Entwurf und weiß, was fehlt.`
          }
          laeuft={laeuft}
          onAusfuehren={(grund) => uebergang('zurueckgeben', grund)}
          onAbbrechen={() => setGefragt(null)}
        />
      )}
      {gefragt === 'stornieren' && (
        <MitGrund
          uebergang="stornieren"
          pflicht
          hinweis={`${r.nummer} wird storniert und bleibt mit dem Grund nachlesbar. Die Nummer wird nicht neu vergeben.`}
          laeuft={laeuft}
          onAusfuehren={(grund) => uebergang('stornieren', grund)}
          onAbbrechen={() => setGefragt(null)}
        />
      )}

      {r.erlaubt.aendern && r.unterschrift && (
        <p className="mt-3 text-sm text-slate-600">
          Zum Bearbeiten zuerst die Unterschrift entfernen – sie gilt für den Inhalt, wie er ist.
        </p>
      )}
    </>
  );
}
